import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { gqlClient } from '@/lib';
import { useScheduleContext } from '@/pages/Schedule/contexts';
import { EntriesContextFragment } from '@/pages/Schedule/contexts/EntriesContext/EntriesContextFragment';
import { useSchedulePermissions } from '@/pages/Schedule/hooks';
import type { DecoratedEntry, DecoratedInstance } from '@/pages/Schedule/types';
import {
  createDecoratedEntry,
  getEntryInstances,
} from '@/pages/Schedule/utils';
import { createEntryQueryKey } from '@/utils/queryKeys';
import type {
  GetEntryQuery,
  GetEntryQueryVariables,
} from './useEntry.generated';

const query = gql`
  ${EntriesContextFragment}
  query GetEntry(
    $scheduleId: ID!
    $entryId: ID!
    $includeMessages: Boolean!
    $includeRsvps: Boolean!
  ) {
    getEntry(scheduleId: $scheduleId, entryId: $entryId) {
      ...EntriesContextFragment
    }
  }
`;

type Data = {
  entry: DecoratedEntry;
  instance: DecoratedInstance;
};

export const useEntry = (
  entryId: string | undefined,
  instanceTimestamp: string | undefined
): Data | undefined => {
  const { scheduleId, timeZone: scheduleTimeZone } = useScheduleContext();
  const { hasSchedulePermission } = useSchedulePermissions();

  const selector = useCallback(
    (data: GetEntryQuery): Data => {
      const entry = createDecoratedEntry(data.getEntry, scheduleTimeZone);
      const instanceDate = instanceTimestamp
        ? DateTime.fromMillis(parseInt(instanceTimestamp))
        : entry.recurrences[0].startDate;

      const instances = getEntryInstances(
        entry,
        instanceDate,
        instanceDate,
        scheduleTimeZone
      );

      return {
        entry,
        instance: instances[0],
      };
    },
    [scheduleTimeZone, instanceTimestamp]
  );

  const result = useQuery({
    queryKey: createEntryQueryKey(scheduleId, entryId),
    select: selector,
    placeholderData: keepPreviousData,
    enabled: !!entryId,
    queryFn: () =>
      gqlClient.request<GetEntryQuery, GetEntryQueryVariables>(query, {
        scheduleId,
        entryId: entryId!, // ok to ignore parser due to the `enabled` option above
        includeRsvps: hasSchedulePermission('RSVP'),
        includeMessages: hasSchedulePermission('MESSAGES'),
      }),
  });

  return result.data;
};
